import type { BotLevel, GameId } from "../constants";
import type { Rng } from "../rng";

/** Thrown by game modules for illegal actions. The message is safe to show to the player (Danish). */
export class GameError extends Error {}

export interface GameEvent {
  kind: string;
  seat?: number;
  data?: Record<string, unknown>;
}

export type Winner = number | "draw" | null;

/**
 * A pure(ish) turn-based board game. State is plain JSON (stored server-side), mutated by apply/penalty/pass.
 * All gating by quiz answers happens in the engine wrapper (src/server/engine/turn.ts) – the module only
 * knows the board rules. Bots receive ONLY their seat's view and so cannot read hidden information.
 */
export interface TurnGameModule<S, A, V> {
  id: GameId;
  create(rng: Rng, players: number, opts?: { rounds?: number }): S;
  phase(s: S): "setup" | "play" | "over";
  /** Seat that must act in 'play' phase. */
  turn(s: S): number;
  /** Seats that still have to act during 'setup' (simultaneous). */
  setupSeats(s: S): number[];
  /** Validate and apply an action. Mutates s. Throws GameError when illegal. */
  apply(s: S, seat: number, a: A, rng: Rng): GameEvent[];
  /**
   * Wrong answer / question timeout: apply the game's penalty and pass the turn.
   * A module may instead return an event of kind 'protected': the penalty does not apply and the same seat may act now.
   */
  penalty(s: S, seat: number, rng: Rng): GameEvent[];
  /** Action timeout (answered correctly but did not act): pass the turn. */
  pass(s: S, seat: number): GameEvent[];
  /** True when the same seat keeps acting WITHOUT a new question (e.g. a freshly drawn card that may still be played). */
  continues?(s: S): boolean;
  winner(s: S): Winner;
  view(s: S, seat: number): V;
  /**
   * Optional: which question (purpose string) must the acting seat answer before the next action? null = none.
   * Without this hook every action is preceded by a question. Needs `resolve`.
   */
  needsQuestion?(s: S): string | null;
  /** Optional: apply the outcome of the gating question (right or wrong). Replaces the default penalty/action flow. */
  resolve?(s: S, seat: number, correct: boolean, rng: Rng): GameEvent[];
  /** Optional: how long the acting seat has per action (ms). */
  actionMs?: number;
  /** Choose an action using only the seat's own view. */
  bot(view: V, seat: number, level: BotLevel, rng: Rng): A;
}
